import { useCallback, useEffect, useRef, useState } from "react";
import { isValidIfsc, lookupIfsc, normalizeIfsc } from "../utils/ifsc";

/**
 * Look a branch up as an IFSC is typed, once per code.
 *
 * The "once" is the point of this hook. The effect is keyed on the normalised
 * code, so it runs when the code CHANGES, not on every keystroke or re-render —
 * and a module-level cache catches the rest: deleting a character and typing it
 * back, or coming back to the tab later, answers from memory instead of the
 * network. Only successes are cached, so a retry after a failure really retries.
 *
 * @param {string} value the raw field value
 * @returns {{status: 'idle'|'loading'|'done'|'error', details: object|null,
 *            error: {kind: string, message: string}|null, retry: () => void}}
 */

// Bounded so a long session can't grow it without limit; branches are tiny and
// never change within a session, which is what makes caching them safe at all.
const CACHE_LIMIT = 50;
const cache = new Map();

/**
 * Forget every remembered branch.
 *
 * A test seam, and the reason it exists: the cache is module state, so without
 * it one test's lookup silently answers the next one's and a "did we call the
 * service?" assertion passes or fails depending on test order. The app never
 * needs this — branches don't change mid-session.
 */
export const clearIfscCache = () => cache.clear();

const remember = (ifsc, details) => {
  if (cache.size >= CACHE_LIMIT) {
    cache.delete(cache.keys().next().value);
  }
  cache.set(ifsc, details);
};

const IDLE = { status: "idle", details: null, error: null };

export default function useIfscLookup(value) {
  const ifsc = normalizeIfsc(value);
  const valid = isValidIfsc(ifsc);
  const [state, setState] = useState(IDLE);
  // Bumped by retry() to re-run the effect for the same code.
  const [attempt, setAttempt] = useState(0);
  const abortRef = useRef(null);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    // Stop whatever the previous code started: its answer is no longer wanted.
    abortRef.current?.abort();
    abortRef.current = null;

    if (!valid) {
      setState(IDLE);
      return undefined;
    }

    const cached = cache.get(ifsc);
    if (cached) {
      setState({ status: "done", details: cached, error: null });
      return undefined;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    let live = true;

    setState({ status: "loading", details: null, error: null });

    lookupIfsc(ifsc, { signal: controller.signal })
      .then((details) => {
        remember(ifsc, details);
        if (live) setState({ status: "done", details, error: null });
      })
      .catch((err) => {
        // An abort means the field moved on; the effect for the new code owns
        // the state now and must not be overwritten by this one.
        if (!live || controller.signal.aborted) return;
        setState({
          status: "error",
          details: null,
          error: {
            kind: err?.kind || "network",
            message: err?.message || "The lookup failed.",
          },
        });
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [ifsc, valid, attempt]);

  // A field that unmounts mid-lookup (tab switch, form closed) leaves nothing
  // in flight.
  useEffect(() => () => abortRef.current?.abort(), []);

  return { ...state, retry };
}
